import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface ComponentNode {
  id: string;
  name: string;
  icon: string;
  x: number;
  y: number;
  color: string;
  layer: number;
}

interface ConnectionLine {
  from: string;
  to: string;
  label: string;
}

const NODES: ComponentNode[] = [
  { id: 'client', name: '客户端', icon: '📱', x: 960, y: 200, color: '#4ECDC4', layer: 0 },
  { id: 'cdn', name: 'CDN', icon: '🌐', x: 560, y: 350, color: '#45B7AF', layer: 1 },
  { id: 'lb', name: '负载均衡', icon: '⚖️', x: 960, y: 350, color: '#FFD700', layer: 1 },
  { id: 'gateway', name: 'API网关', icon: '🚪', x: 1360, y: 350, color: '#FF8C5A', layer: 1 },
  { id: 'svc1', name: '用户服务', icon: '👤', x: 560, y: 540, color: '#667EEA', layer: 2 },
  { id: 'svc2', name: '订单服务', icon: '📦', x: 960, y: 540, color: '#9B59B6', layer: 2 },
  { id: 'svc3', name: '支付服务', icon: '💳', x: 1360, y: 540, color: '#2ECC71', layer: 2 },
  { id: 'cache', name: 'Redis缓存', icon: '⚡', x: 560, y: 730, color: '#E74C3C', layer: 3 },
  { id: 'db', name: 'MySQL主库', icon: '🗄️', x: 960, y: 730, color: '#3498DB', layer: 3 },
  { id: 'mq', name: '消息队列', icon: '📨', x: 1360, y: 730, color: '#F39C12', layer: 3 },
];

const CONNECTIONS: ConnectionLine[] = [
  { from: 'client', to: 'cdn', label: '静态资源' },
  { from: 'client', to: 'lb', label: 'API请求' },
  { from: 'lb', to: 'gateway', label: '路由分发' },
  { from: 'gateway', to: 'svc1', label: 'REST' },
  { from: 'gateway', to: 'svc2', label: 'gRPC' },
  { from: 'gateway', to: 'svc3', label: 'REST' },
  { from: 'svc1', to: 'cache', label: '缓存读写' },
  { from: 'svc2', to: 'db', label: '持久化' },
  { from: 'svc3', to: 'mq', label: '异步事件' },
];

const PRINCIPLES = [
  { title: '高可用', desc: '多副本 + 自动故障转移', icon: '🛡️', color: '#4ECDC4' },
  { title: '可扩展', desc: '水平扩展 + 无状态服务', icon: '📈', color: '#FFD700' },
  { title: '高性能', desc: '缓存 + 异步 + CDN加速', icon: '⚡', color: '#FF6B35' },
  { title: '一致性', desc: '分布式事务 + 最终一致性', icon: '🔄', color: '#667EEA' },
];

export const SystemDesignAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  const nodeMap = React.useMemo(() => {
    const m: Record<string, ComponentNode> = {};
    NODES.forEach((n) => { m[n.id] = n; });
    return m;
  }, []);

  return (
    <AbsoluteFill
      style={{
        background: '#0B0E17',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.04,
          backgroundImage:
            'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: 20,
          left: 50,
          opacity: titleProg,
          transform: `translateY(${interpolate(titleProg, [0, 1], [-25, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 56,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #667EEA, #4ECDC4)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          系统设计架构图
        </h1>
        <p style={{ fontSize: 28, margin: '6px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          System Design · 微服务架构全景
        </p>
      </div>

      <svg
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      >
        {CONNECTIONS.map((conn, i) => {
          const fromNode = nodeMap[conn.from];
          const toNode = nodeMap[conn.to];
          if (!fromNode || !toNode) return null;

          const connProg = interpolate(frame, [50 + i * 12, 70 + i * 12], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });

          const x1 = fromNode.x;
          const y1 = fromNode.y + 40;
          const x2 = fromNode.x + (toNode.x - fromNode.x) * connProg;
          const y2 = fromNode.y + 40 + (toNode.y - 40 - fromNode.y - 40) * connProg;

          return (
            <g key={`${conn.from}-${conn.to}`}>
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="rgba(255,255,255,0.12)"
                strokeWidth={2}
                strokeDasharray="6 4"
              />
              {connProg > 0.5 && (
                <text
                  x={(fromNode.x + toNode.x) / 2}
                  y={fromNode.y + (toNode.y - fromNode.y) * 0.65}
                  fontSize={20}
                  fill="rgba(255,255,255,0.3)"
                  textAnchor="middle"
                >
                  {conn.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {NODES.map((node, i) => {
        const nodeProg = spring({
          frame: frame - 20 - node.layer * 18 - (i % 3) * 6,
          fps,
          config: { damping: 12, stiffness: 140 },
        });

        return (
          <div
            key={node.id}
            style={{
              position: 'absolute',
              left: node.x - 90,
              top: node.y - 40,
              width: 180,
              textAlign: 'center',
              opacity: nodeProg,
              transform: `scale(${nodeProg})`,
            }}
          >
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: 18,
                background: `${node.color}20`,
                border: `2px solid ${node.color}50`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 40,
                margin: '0 auto 8px',
                boxShadow: `0 4px 16px ${node.color}20`,
              }}
            >
              {node.icon}
            </div>
            <div style={{ fontSize: 24, fontWeight: 600, color: node.color }}>{node.name}</div>
          </div>
        );
      })}

      <div
        style={{
          position: 'absolute',
          bottom: 60,
          left: 50,
          right: 50,
          display: 'flex',
          gap: 16,
        }}
      >
        {PRINCIPLES.map((p, i) => {
          const pProg = spring({
            frame: frame - 140 - i * 12,
            fps,
            config: { damping: 14, stiffness: 120 },
          });

          return (
            <div
              key={p.title}
              style={{
                flex: 1,
                padding: '14px 18px',
                background: `${p.color}10`,
                border: `1px solid ${p.color}30`,
                borderRadius: 14,
                opacity: pProg,
                transform: `translateY(${interpolate(pProg, [0, 1], [20, 0])}px)`,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <span style={{ fontSize: 32 }}>{p.icon}</span>
              <div>
                <div style={{ fontSize: 24, fontWeight: 700, color: p.color }}>{p.title}</div>
                <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.4)' }}>{p.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
